import React from 'react';

import {Button, Container, Paper, Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useQueryClient, useMutation} from 'react-query';

import API, {AccountUpdateRequest, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {ConfirmationDialog, Head, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  errorNoUser: {
    id: 'pages.member.PasswordUpdatePage.error.noUesr',
    defaultMessage: 'user not found',
  },
  errorInvalidValue: {
    id: 'pages.member.PasswordUpdatePage.error.invalidValue',
    defaultMessage: 'invalid value found',
  },
  errorInvalidEmail: {
    id: 'pages.member.PasswordUpdatePage.error.invalidEmail',
    defaultMessage: 'invalid email',
  },
  headerTitle: {
    id: 'pages.member.PasswordUpdatePage.header.title',
    defaultMessage: 'Change Password',
  },
  textContent: {
    id: 'pages.member.PasswordUpdatePage.text.content',
    defaultMessage: 'Please enter your current email and password, and the new password.',
  },
  inputEmailTitle: {
    id: 'pages.member.PasswordUpdatePage.input.emailTitle',
    defaultMessage: 'Email',
  },
  inputPasswordTitle: {
    id: 'pages.member.PasswordUpdatePage.input.passwordTitle',
    defaultMessage: 'Password',
  },
  inputNewPasswordTitle: {
    id: 'pages.member.PasswordUpdatePage.input.newPasswordTitle',
    defaultMessage: 'New Password',
  },
  inputPasswordPlaceholder: {
    id: 'pages.member.PasswordUpdatePage.input.passwordPlaceholder',
    defaultMessage: 'Enter your current password',
  },
  inputNewPasswordPlaceholder: {
    id: 'pages.member.PasswordUpdatePage.input.newPasswordPlaceholder',
    defaultMessage: 'Enter your new password',
  },
  buttonCancel: {
    id: 'pages.member.PasswordUpdatePage.button.cancel',
    defaultMessage: 'Cancel',
  },
  buttonChangePassword: {
    id: 'pages.member.PasswordUpdatePage.button.changePassword',
    defaultMessage: 'Change Password',
  },
  dialogConfirmationTitle: {
    id: 'pages.member.PasswordUpdatePage.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogConfirmationContent: {
    id: 'pages.member.PasswordUpdatePage.dialog.confirmationContent',
    defaultMessage:
      'Are you really going to change your password? After the change request is submitted, ' +
      'your account is invalidated and redirected to the login page. Please sign in with ' +
      'your new password.',
  },
});

function PasswordUpdatePage(): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {account} = useAuth();
  const {showSuccess} = useSnackbar();
  const {register, formState, handleSubmit: validate} = useForm();
  const {errors} = formState;
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const [email, setEmail] = React.useState<string>();
  const [password, setPassword] = React.useState<string>();
  const [newPassword, setNewPassword] = React.useState<string>();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
  const {mutate: updateAccount, isLoading} = useMutation<unknown, unknown, AccountUpdateRequest>(
    (props: AccountUpdateRequest) => API.Accounts.update(csrfToken, props),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(API.Accounts.CacheKey);
        showSuccess('Your password was successfully updated');
        navigate('/settings');
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleGoBack = React.useCallback(() => navigate('/settings'), [navigate]);
  const handleSubmit = React.useCallback(() => {
    if (!account) {
      showError(intl.formatMessage(msgs.errorNoUser));
      return;
    }
    if (!email || !password || !newPassword) {
      showError(intl.formatMessage(msgs.errorInvalidValue));
      return;
    }
    setConfirmationDialogOpen(true);
  }, [email, password, newPassword, showError, intl, , account]);
  const handleConfirmationCancel = React.useCallback(() => setConfirmationDialogOpen(false), []);
  const handleConfirmationOk = React.useCallback(async () => {
    setConfirmationDialogOpen(false);
    if (!account) {
      showError(intl.formatMessage(msgs.errorNoUser));
      return;
    }
    if (!email || !password || !newPassword) {
      showError(intl.formatMessage(msgs.errorInvalidValue));
      return;
    }
    updateAccount({email, password, newPassword});
  }, [email, password, newPassword, showError, intl, account, updateAccount]);

  return (
    <Container sx={{display: 'flex', justifyContent: 'center'}}>
      <Head title={intl.formatMessage(msgs.headerTitle)} />
      <form onSubmit={validate(handleSubmit)}>
        <Paper sx={{width: 400, mt: 8, p: 3}}>
          <Stack gap={3}>
            <Typography align="center" variant="h6">
              {intl.formatMessage(msgs.headerTitle)}
            </Typography>
            {intl.formatMessage(msgs.textContent)}
            <ValidatableField
              id="email"
              autoComplete="email"
              label={intl.formatMessage(msgs.inputEmailTitle)}
              fullWidth
              disabled={isLoading}
              {...{errors, register}}
              registerOptions={{
                required: true,
                maxLength: 128,
                onChange: (e) => setEmail(e.target.value),
              }}
            />
            <ValidatableField
              id="password"
              autoComplete="current-password"
              label={intl.formatMessage(msgs.inputPasswordTitle)}
              placeholder={intl.formatMessage(msgs.inputPasswordPlaceholder)}
              fullWidth
              type="password"
              disabled={isLoading}
              {...{errors, register}}
              registerOptions={{
                required: true,
                maxLength: 128,
                onChange: (e) => setPassword(e.target.value),
              }}
            />
            <ValidatableField
              id="newPassword"
              autoComplete="new-password"
              label={intl.formatMessage(msgs.inputNewPasswordTitle)}
              placeholder={intl.formatMessage(msgs.inputNewPasswordPlaceholder)}
              fullWidth
              type="password"
              disabled={isLoading}
              {...{errors, register}}
              registerOptions={{
                required: true,
                maxLength: 128,
                onChange: (e) => setNewPassword(e.target.value),
              }}
            />
            <Stack direction="row" gap={3}>
              <Button disabled={isLoading} fullWidth onClick={handleGoBack}>
                {intl.formatMessage(msgs.buttonCancel)}
              </Button>
              <Button fullWidth disabled={isLoading} type="submit">
                {intl.formatMessage(msgs.buttonChangePassword)}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </form>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        title={intl.formatMessage(msgs.dialogConfirmationTitle)}
        description={intl.formatMessage(msgs.dialogConfirmationContent)}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationOk}
      />
    </Container>
  );
}

export default React.memo(PasswordUpdatePage);
